import clsx from 'clsx';
import type * as React from 'react';

import type { ContentRef } from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    clipboard,
    Popover,
    PopoverRoot,
    PopoverTrigger,
    renderIcon,
    Toolbar,
} from '@gitbook/spine-react';

import {
    type UIResolvedContentReference,
    useContentRefNavigationProps,
} from '../../ContentReferences';
import { InlineCard, type InlineCardProps } from '../inlines';

/**
 * Read-only inline link with a preview.
 */
export function InlineContentRefPreviewPopover(
    props: InlineContentRefPreviewCardProps & {
        children: React.ReactElement;
    }
) {
    const { children, ...rest } = props;
    return (
        <PopoverRoot triggerAction="hover">
            <PopoverTrigger>{children}</PopoverTrigger>
            <Popover>
                <InlineContentRefPreviewCard {...rest} />
            </Popover>
        </PopoverRoot>
    );
}

export type InlineContentRefPreviewCardProps = PreviewCardTitleProps &
    Pick<InlineCardProps, 'label'> & {
        resolved: UIResolvedContentReference;
        contentRef: ContentRef;
        actions?: React.ReactNode;
    };

/**
 * Preview card displayed in read-only and non-edit mode.
 */
export function InlineContentRefPreviewCard(props: InlineContentRefPreviewCardProps) {
    const { actions, contentRef, resolved, label } = props;
    return (
        <InlineCard className="pr-2" label={`${label} preview`}>
            <div className="flex items-center gap-2">
                <div className="relative flex-1 overflow-hidden">
                    <PreviewCardTitle contentRef={contentRef} resolved={resolved} />
                </div>
                <Toolbar unstyled size="small" className="gap-0.5" aria-label="Actions">
                    {resolved.resolution && contentRef.kind !== 'user' ? (
                        <Button
                            size="small"
                            kind="blank"
                            iconOnly
                            leadingIcon={Icon.Copy}
                            label="Copy"
                            onPress={() => {
                                clipboard.writeText(resolved.href);
                            }}
                        />
                    ) : null}
                    {actions}
                </Toolbar>
            </div>
        </InlineCard>
    );
}

type PreviewCardTitleProps = { resolved: UIResolvedContentReference; contentRef: ContentRef };

/**
 * Preview card title that acts as a link if resolved.
 */
function PreviewCardTitle(props: PreviewCardTitleProps) {
    const { resolved, contentRef } = props;
    const navProps = useContentRefNavigationProps(props.resolved);

    if (contentRef.kind === 'user') {
        return (
            <div className="font-medium">
                <span className="mr-2 inline-flex align-middle">
                    {renderIcon(resolved.icon || resolved.secondaryIcon)}
                </span>
                {resolved.title}
            </div>
        );
    }

    if (!resolved.resolution) {
        return resolved.title;
    }

    const isTitleURL = resolved.href === resolved.title;

    // If title is an URL, we display it on one line, truncated.
    if (isTitleURL) {
        return (
            <a
                {...navProps}
                target="_blank"
                className={clsx(
                    'fill-[--sp-color-text-primary] font-medium text-primary',
                    'flex items-center truncate'
                )}
            >
                <span className="flex-1 truncate">{resolved.href}</span>
                <Icon.ExternalLink className="ml-1.5 size-3 shrink-0" />
            </a>
        );
    }

    // Prevent a line break to happen before the icon.
    const titleArr = resolved.title.trim().split(' ');
    const lastWord = titleArr.pop();
    const restOfTitle = titleArr.join(' ');
    return (
        <a
            {...navProps}
            className={clsx('fill-[--sp-color-text-primary] font-medium text-primary')}
        >
            <span>
                {restOfTitle}
                {lastWord ? ' ' : ''}
            </span>
            <span className="inline-flex max-w-full items-center whitespace-nowrap">
                <span className="truncate">{lastWord}</span>
                <Icon.ExternalLink className="ml-1.5 size-3 shrink-0" />
            </span>
        </a>
    );
}
